import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ShoppingBag, User, Menu, X, LogOut } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Hair Care", to: "/shop?cat=Hair%20Care" },
  { label: "Skin Care", to: "/shop?cat=Skin%20Care" },
  { label: "Serums", to: "/shop?cat=Serums" },
  { label: "Salon Pro", to: "/shop?cat=Professional%20Salon%20Essentials" },
  { label: "Salon Partner", to: "/salon-partner", highlight: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { count, setOpen } = useCart();
  const { user, salon, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();
  const isApprovedPartner = salon?.status === "approved";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const q = searchQuery.trim();
    if (!q) return;
    setSearchOpen(false);
    setSearchQuery("");
    navigate(`/shop?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <div className="w-full bg-black text-white text-[11px] tracking-[0.3em] uppercase text-center py-2 font-body" data-testid="top-strip">
        <span className="opacity-80">Complimentary luxury shipping on orders over ₹499 · </span>
        <Link to="/salon-partner" className="opacity-90 hover:text-[#C9A961] transition-colors underline-offset-4 hover:underline" data-testid="top-strip-salon-cta">
          Salon Partner Program →
        </Link>
      </div>

      {isApprovedPartner && (
        <div className="w-full bg-[#C9A961] text-black text-[11px] tracking-[0.3em] uppercase text-center py-1.5 font-body" data-testid="wholesale-banner">
          ★ Salon Partner mode — Wholesale pricing active
        </div>
      )}

      <header
        className={`fixed top-[28px] left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass" : "bg-white/0"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 -ml-2"
            data-testid="mobile-menu-button"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>

          <nav className="hidden md:flex items-center gap-9" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className={`text-[12px] tracking-[0.22em] uppercase font-body transition-colors ${
                  l.highlight
                    ? "text-[#C9A961] hover:text-black border-b border-[#C9A961]/40 pb-0.5"
                    : "text-black/85 hover:text-[#C9A961]"
                }`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <Link to="/" className="flex items-center" data-testid="logo-home-link">
            <img src={LOGO_URL} alt="Blisson Professional" className="h-12 md:h-14 w-auto object-contain mix-blend-multiply" />
          </Link>

          <div className="flex items-center gap-5">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 hover:text-[#C9A961] transition-colors"
              data-testid="search-button"
              aria-label="Search products"
            >
              <Search size={19} strokeWidth={1.4} />
            </button>

            <div className="relative">
              <button
                onClick={() => (user ? setUserMenuOpen((s) => !s) : loginWithGoogle())}
                className="p-2 hover:text-[#C9A961] transition-colors flex items-center gap-2"
                data-testid="user-account-button"
                aria-label="Account"
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover ring-1 ring-black/10" />
                ) : (
                  <User size={19} strokeWidth={1.4} />
                )}
              </button>

              <AnimatePresence>
                {user && userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 mt-3 w-64 bg-white border border-black/5 luxury-shadow"
                    data-testid="user-menu"
                  >
                    <div className="p-5 border-b border-black/5">
                      <div className="font-serif-display text-lg">{user.name}</div>
                      <div className="text-xs text-[#5C5C5C] mt-0.5">{user.email}</div>
                    </div>
                    {user.is_admin && (
                      <Link
                        to="/admin"
                        onClick={() => setUserMenuOpen(false)}
                        className="block px-5 py-3 text-[12px] tracking-[0.22em] uppercase hover:bg-[#F8F5F0] border-b border-black/5"
                        data-testid="user-menu-admin-link"
                      >
                        <span className="text-[#C9A961]">★</span> Admin Panel
                      </Link>
                    )}
                    <Link
                      to="/salon-partner"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-5 py-3 text-[12px] tracking-[0.22em] uppercase hover:bg-[#F8F5F0] border-b border-black/5"
                      data-testid="user-menu-salon-link"
                    >
                      {salon?.status === "approved" ? <><span className="text-[#C9A961]">✓</span> Wholesale Active</>
                        : salon?.status === "pending" ? <><span className="text-[#C9A961]">●</span> Application Pending</>
                        : <><span className="text-[#C9A961]">★</span> Salon Partner</>}
                    </Link>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout();
                        navigate("/");
                      }}
                      className="w-full text-left px-5 py-3 text-[12px] tracking-[0.22em] uppercase hover:bg-[#F8F5F0] flex items-center gap-3"
                      data-testid="logout-button"
                    >
                      <LogOut size={15} /> Sign Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => setOpen(true)}
              className="p-2 relative hover:text-[#C9A961] transition-colors"
              data-testid="cart-toggle-button"
              aria-label="Cart"
            >
              <ShoppingBag size={19} strokeWidth={1.4} />
              {count > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-[#C9A961] text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center"
                  data-testid="cart-badge"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-white/95 backdrop-blur-xl flex items-start justify-center pt-32 px-6"
            data-testid="search-overlay"
            onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
          >
            <motion.form
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              onSubmit={submitSearch}
              className="w-full max-w-2xl"
              data-testid="search-form"
            >
              <div className="overline mb-4 text-center">Search the Collection</div>
              <div className="flex items-center border-b-2 border-black/60 pb-3 gap-3">
                <Search size={22} strokeWidth={1.2} className="text-[#5C5C5C]" />
                <input
                  autoFocus
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Hair oil, gold facial kit, vitamin c serum…"
                  className="flex-1 bg-transparent outline-none text-2xl md:text-3xl font-serif-display tracking-tight placeholder:text-[#B8B8B8]"
                  data-testid="search-input"
                />
                <button
                  type="button"
                  onClick={() => setSearchOpen(false)}
                  className="p-2 text-[#5C5C5C] hover:text-black transition-colors"
                  data-testid="search-close"
                  aria-label="Close search"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mt-3 text-xs text-[#5C5C5C] text-right">Press <kbd className="border border-black/20 px-1.5 py-0.5 rounded text-[10px]">Enter</kbd> to search</div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.35 }}
            className="fixed inset-0 bg-white z-[60] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between p-6 border-b border-black/5">
              <img src={LOGO_URL} alt="Blisson" className="h-10 mix-blend-multiply" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
                <X size={22} />
              </button>
            </div>
            <nav className="flex flex-col p-8 gap-5" data-testid="mobile-menu-links">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className={`font-serif-display text-3xl flex items-center gap-3 ${l.highlight ? "text-[#C9A961]" : ""}`}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.highlight && <span className="text-base">★</span>}
                  {l.label}
                </Link>
              ))}

              {/* Quick utility row */}
              <div className="mt-6 pt-6 border-t border-black/10 space-y-4">
                <button
                  onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
                  className="flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-black/70"
                  data-testid="mobile-menu-search"
                >
                  <Search size={16} strokeWidth={1.4} /> Search Products
                </button>
                {!user ? (
                  <button
                    onClick={() => { setMobileOpen(false); loginWithGoogle(); }}
                    className="flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-black/70"
                    data-testid="mobile-menu-login"
                  >
                    <User size={16} strokeWidth={1.4} /> Sign In with Google
                  </button>
                ) : (
                  <button
                    onClick={() => { setMobileOpen(false); logout(); }}
                    className="flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-black/70"
                    data-testid="mobile-menu-logout"
                  >
                    <User size={16} strokeWidth={1.4} /> Sign Out
                  </button>
                )}
                <a
                  href="https://wa.me/917383899003?text=Hi%20Blisson%2C%20I%20have%20a%20query"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase text-[#25D366]"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-menu-whatsapp"
                >
                  💬 WhatsApp Support
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
